import React, {useEffect, useRef, useState} from 'react';
import {Button} from "react-bootstrap";
import Page from "./Page";
import {PagesResponse, Rectangle} from "./interfaces";
import Submission from "./Submission";
import {API_URL, JWT, OFFLINE_DEVELOPMENT} from "./config";


function App() {
    const [pagesResponse, setPagesResponse] = useState<PagesResponse | null>(null);
    const [mode, setMode] = useState<"blacken" | "highlight">("blacken");
    const [prevRectangleStates, setPrevRectangleStates] = useState<Rectangle[][][] | null>(null)
    const [selectedRectangle, setSelectedRectangle] = useState<Rectangle | null>(null)
    const [rectanglesByPage, setRectanglesByPage] = useState<Rectangle[][]>([[], []]);
    const [editorUuid, setEditorUuid] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [evidenceUuid, setEvidenceUuid] = useState<string | null>(null)
    const imagesAreFetched = useRef(false);
    const unloadListener = useRef<OnBeforeUnloadEventHandler>();

    //load from case uuid, if in URL
    useEffect(() => {
        if (!imagesAreFetched.current) {
            imagesAreFetched.current = true;
            (async () => {
                console.log("loading from URL")
                let params = (new URL(document.location.toString())).searchParams;
                let evidenceUuid = params.get('id');
                setIsLoading(true);
                if (evidenceUuid) {
                    await loadFromServer(evidenceUuid)

                    //set unload listener
                    const beforeUnloadListener = (event: BeforeUnloadEvent) => {
                        event.preventDefault();
                        return event.returnValue = "Möchten Sie den NetzBeweis-Editor wirklich verlassen?";
                    };
                    unloadListener.current = beforeUnloadListener;
                    window.addEventListener("beforeunload", beforeUnloadListener);
                }
                setIsLoading(false);
            })();
        }
    }, [])

    useEffect(() => {
        const listener = (e: KeyboardEvent) => {
            if (e === undefined || e === null) {
                return;
            }

            if (e.key === "Backspace" || e.key === "Delete") {
                // @ts-ignore
                console.log("del key pressed", selectedRectangle, this)
                removeSelectedRectangle()
            } else if (e.key === "z" && e.ctrlKey) {
                undoLast()
            } else if (e.key === "Escape" && selectedRectangle != null) {
                setSelectedRectangle(null);
            }
        };
        document.addEventListener("keydown", listener)
        return () => document.removeEventListener("keydown", listener);
    }, [selectedRectangle, rectanglesByPage]);  // eslint-disable-line react-hooks/exhaustive-deps

    function removeSelectedRectangle() {
        if (selectedRectangle !== null) {
            //delete from list
            let filteredList = rectanglesByPage.map((rectangles => rectangles.filter(r => r !== selectedRectangle)))
            setRectanglesByPage(filteredList);
            preserveState();
        }
    }

    function preserveState() {
        // Use the callback form of setPrevRectangleStates to avoid timing issues
        let newState = rectanglesByPage;
        setPrevRectangleStates(prevRectangleStates => {
            if (prevRectangleStates === null) {
                return [newState]
            } else {
                return [...prevRectangleStates, newState]
            }
        })
    }

    function undoLast() {
        // Use the callback form of setPrevRectangleStates to avoid timing issues
        setPrevRectangleStates((prevStates) => {
            if (prevStates === null) {
                return prevStates;
            }

            if (prevStates.length === 1 && pagesResponse) {
                setRectanglesByPage(Array.from({length: pagesResponse?.pages.length}, (i) => []))
                return null;
            }

            setRectanglesByPage(prevStates[prevStates.length - 2])
            return prevStates.slice(0, -1)
        })
    }

    async function loadFromServer(evidenceUuid: string) {
        var headers = new Headers();
        headers.append("Authorization", "Bearer " + JWT);
        headers.append("Content-Type", "application/json");

        var requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                //"linkToPDF": "https://www-dev.netzbeweis.com/pdf/NetzBeweis_498c48d7-eca7-4acd-b55c-5ea46ca4c19b.pdf"
                "evidenceUuid": evidenceUuid
            })
        };


        try {
            let json: PagesResponse;
            if (OFFLINE_DEVELOPMENT) {
                json = {"pages":[{"page":0,"url":"https://www-dev.netzbeweis.com/pdf/editor/page_55149d97-3388-4cad-a049-64e3461a3b50_1.png"},{"page":1,"url":"https://www-dev.netzbeweis.com/pdf/editor/page_55149d97-3388-4cad-a049-64e3461a3b50_2.png"},{"page":2,"url":"https://www-dev.netzbeweis.com/pdf/editor/page_55149d97-3388-4cad-a049-64e3461a3b50_3.png"},{"page":3,"url":"https://www-dev.netzbeweis.com/pdf/editor/page_55149d97-3388-4cad-a049-64e3461a3b50_4.png"},{"page":4,"url":"https://www-dev.netzbeweis.com/pdf/editor/page_55149d97-3388-4cad-a049-64e3461a3b50_5.png"},{"page":5,"url":"https://www-dev.netzbeweis.com/pdf/editor/page_55149d97-3388-4cad-a049-64e3461a3b50_6.png"}],"uuid":"55149d97-3388-4cad-a049-64e3461a3b50","demo":false}
            } else {
                let response = await fetch(API_URL + "/editor/getPDFAsImages", requestOptions)
                json = (await response.json()) as PagesResponse;
            }
            setPagesResponse(json)
            setRectanglesByPage(Array.from({length: json.pages.length}, (i) => []))
            setEditorUuid(json.uuid)
            console.log("set isloading false")
            setIsLoading(false)

            //load image
            console.log(json)
        } catch (e) {
            console.log(e)
        }
    }

    useEffect(() => {
        pagesResponse?.pages.forEach((page, i) => {
            const canvas = document.getElementsByClassName('drawCanvas')[i] as HTMLImageElement;
            canvas.addEventListener('load', (e: Event) => {
                canvas.width = canvas.naturalWidth;
                canvas.height = canvas.naturalHeight;
                console.log("nature: " + canvas.naturalWidth)
            })
            canvas.src = page.url;
        })
    }, [pagesResponse])

    function save() {
        console.log("user output: ", rectanglesByPage);
        var myHeaders = new Headers();
        myHeaders.append("Authorization", "Bearer " + JWT);
        myHeaders.append("Content-Type", "application/json");

        var raw = JSON.stringify({
            "uuid": editorUuid,
            "pageEdits": rectanglesByPage.map((r, i) => {
                return {
                    page: i,
                    rectangles: r
                }
            })
        });

        var requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: raw
        };

        setIsLoading(true);

        (async () => {
            let response = await fetch(API_URL + "/editor/finishEdit", requestOptions);
            let json = await response.json();
            let evidenceUuid = json.uuid;

            setIsLoading(false); //from here on, submission component will take over

            //poll uuid until something is here
            setEvidenceUuid(evidenceUuid); //this will trigger submission alert via effect
        })()
    }

    // @ts-ignore
    return (
        <div>
            {(evidenceUuid !== null) && (
                <Submission evidenceUuid={evidenceUuid} unloadListener={unloadListener}></Submission>
            )}

            {isLoading &&
                <div className={"fullPageOverlay d-flex align-items-center justify-content-center flex-column"}>
                    <div className="spinner-border" style={{width: "3rem", height: "3rem"}} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className={"mt-4"}>Dokument wird geladen...</p>
                </div>
            }
            <div className="container">
                <div className={"sticky-top bg-light p-3"}>
                    <div className={"row"}>
                        <div className={"col-sm-12"}>
                            <h1>Schwärzen und Hervorheben</h1>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col-sm-12 my-2"}>
                            {/* <Button type={"button"} variant={"outline-secondary"} onClick={loadFromServer} className={"me-2"}>Laden</Button> */}

                        </div>
                    </div>
                    <div className={"row "}>
                        <div className={"col-sm-12"}>
                            <Button className={"me-2 btn-dark buttonDarken"} disabled={mode === "blacken"}
                                    onClick={() => setMode("blacken")}>Schwärzen</Button>
                            <Button className={"me-4 btn-warning buttonHighlight"} disabled={mode === "highlight"}
                                    onClick={() => setMode("highlight")}>Hervorheben</Button>
                            <Button onClick={undoLast} disabled={prevRectangleStates === null}
                                    variant={"outline-secondary"}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                                     className="bi bi-arrow-counterclockwise" viewBox="0 0 16 16">
                                    <path fillRule="evenodd"
                                          d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z"/>
                                    <path
                                        d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z"/>
                                </svg>&nbsp;
                                Rückgängig</Button>
                            <Button className={"ms-4"} type={"button"} variant={"outline-primary"} onClick={save}
                                    disabled={prevRectangleStates === null}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                                     className="bi bi-save" viewBox="0 0 16 16">
                                    <path
                                        d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/>
                                </svg>&nbsp;
                                Speichern und Abschließen</Button>
                        </div>
                    </div>
                </div>
                <div className={"row my-3"}>
                    {pagesResponse?.pages.map((val, i) =>
                        <div key={`page${i}`}
                            className={"col-sm-12 " + ((i === 0 || i === (pagesResponse?.pages.length - 1)) ? "d-none" : "")}>
                            <Page
                                  selectedRectangle={selectedRectangle} setSelectedRectangle={setSelectedRectangle}
                                  mode={mode}
                                  demoPage={pagesResponse?.demo && i > 2}
                                  removeSelectedRectangle={removeSelectedRectangle}
                                  rectangles={rectanglesByPage[i]} setRectangles={(r) => {
                                rectanglesByPage[i] = r as Rectangle[];
                                setRectanglesByPage([...rectanglesByPage]) //need to re-write array in order to trigger update
                                preserveState();
                            }}></Page>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default App;
